import { PropsWithChildren, createContext, useEffect, useMemo } from 'react';

import { useScroll } from 'ahooks';
import { useSearchParams } from 'react-router-dom';

import { useScrollCacheContext } from '../../context/ScrollCacheProvider';

import type {
  ReportListContextState,
  ReportListFilterType,
  ReportListTabType,
} from './ReportList.types';

export const ReportListContext = createContext<ReportListContextState>({
  tab: 'list',
  filter: {},
  setTab: () => {},
  setFilter: () => {},
  resetScrollPosition: () => {},
});

export interface ReportListQuery extends ReportListFilterType {
  tab: ReportListTabType;
}

export const ReportListProvider = (props: PropsWithChildren<unknown>) => {
  const { children } = props;
  const scroll = useScroll(document);
  const { scrollCache, setScrollCache } = useScrollCacheContext();
  const [searchParams, setSearchParams] = useSearchParams({ tab: 'list' });

  const tab = (searchParams.get('tab') || 'list') as ReportListTabType;
  const startWorkDate = searchParams.get('startWorkDate') || undefined;
  const endWorkDate = searchParams.get('endWorkDate') || undefined;
  const processes = searchParams.getAll('processes') || undefined;

  const setTab = (value: ReportListTabType) => {
    setSearchParams({ tab: value }, { replace: true });
    window.scrollTo({
      behavior: 'instant',
      top: 0,
    });
  };

  const filter = useMemo(() => {
    return {
      startWorkDate: startWorkDate,
      endWorkDate: endWorkDate,
      processes: processes,
    };
  }, [startWorkDate, endWorkDate, processes]);
  const setFilter = (value: ReportListFilterType) => {
    setSearchParams({ ...value }, { replace: true });
  };

  /**
   * 탭이 변경될 때마다 저장된 스크롤 위치를 적용한다.
   * 이때 스크롤 위치의 기본값은 0이다.
   */
  useEffect(() => {
    const cachedScrollTop = scrollCache[`report-list.${tab}`];
    if (typeof cachedScrollTop !== 'number') return;
    window.scrollTo(0, cachedScrollTop);
  }, [tab]);

  /**
   * 스크롤이 변경될 때마다 탭별 스크롤 위치를 기록한다.
   */
  useEffect(() => {
    setScrollCache(() => ({
      [`report-list.${tab}`]: scroll?.top || 0,
    }));
  }, [scroll?.top]);

  /**
   * 기록된 스크롤 위치와 브라우저 스크롤을 모두 0으로 초기화한다.
   * 기본적으로 필터값이 바뀌었을 때 react-query 내에서 캐시된 데이터가 없을 경우
   * 스크롤 위치를 초기화하는 용도로 사용한다.
   */
  const resetScrollPosition = () => {
    window.scrollTo(0, 0);
    setScrollCache({
      'report-list.list': 0,
      'report-list.photos': 0,
      'report-list.timelapses': 0,
    });
  };

  return (
    <ReportListContext.Provider value={{ tab, filter, setTab, setFilter, resetScrollPosition }}>
      {children}
    </ReportListContext.Provider>
  );
};
